import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Field } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';

import TextField from 'components/fields/TextField';
import DatepickerField from 'components/fields/DatepickerField';
import CheckboxField from 'components/fields/CheckboxField';
import RadioGroupField from 'components/fields/RadioGroupField';
import SelectMenuField from 'components/fields/SelectMenuField';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';
import patientDobToAge from 'utils/patientDobToAge';
import sortInsurance from 'utils/sortInsurance';
import getBusinessAddress from 'utils/print/getBusinessAddress';
import dateFormat from 'config/dateFormat';

import TemplatePreviewFillFormFieldSignature from './TemplatePreviewFillFormFieldSignature';

const styles = theme => ({
  typographyLabel: {
    fontWeight: 500,
    marginBottom: 8,
    paddingBottom: 4,
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
  },
  selectMenuField: {
    minWidth: 231,
  },
  typographySignatureText: {
    fontStyle: 'italic',
  },
  typographyLabelPlaceholder: {
    fontWeight: 700,
    marginTop: 16,
    marginBottom: 8,
    color: theme.palette.grey[600],
  },
  typographyAnswerPlaceholder: {
    border: `1px dashed ${theme.palette.primary.A400}`,
    borderRadius: 4,
  },
  typographyDateStampPlaceholder: {
    marginTop: 16,
  },
  typographyPatientInformationPlaceholder: {
    marginLeft: -64,
    marginRight: -20,
    padding: '16px 112px 0 26px',
    borderTop: `1px solid ${theme.palette.primary.A100}`,
  },
  tablePatientGeneral: {
    width: 'auto',
  },
  table: {
    maxWidth: '100%',
    width: 'auto',
  },
  tableNarrow: {
    width: '100%',
  },
  tableRow: {
    height: 28,
  },
  tableCell: {
    padding: '4px 16px',
    verticalAlign: 'top',
    border: 'none',
    '&:first-child': {
      paddingLeft: 0,
    },
    '&:last-child': {
      paddingRight: 0,
    },
  },
  tableCellVitals: {
    width: 50,
    whiteSpace: 'nowrap',
  },
  typographyInline: {
    display: 'inline',
  },
  logoImageContainer: {
    width: 200,
  },
  logoImage: {
    width: 'auto',
    height: 'auto',
    maxWidth: '100%',
  },
  checkboxField: {
    marginTop: -8,
    marginBottom: -8,
  },
});

const TemplatePreviewFillFormField = ({
  name,
  Text,
  Choices,
  patient,
  order,
  business,
  classes,
  ...otherProps
}) => {
  let insurances;
  let prescribers;
  let vitals;
  let diagnoses;
  if (patient) {
    prescribers = patient.Prescriber;
    vitals = patient.Vitals
    diagnoses = patient.Diagnosis;
    insurances = patient['Insurance Information'].filter(item =>
      item['Insurance Type'] !== 'Self');
  }
  if (order) {
    prescribers = [order.Prescriber];
    vitals = order.Vitals;
    diagnoses = order.Diagnosis;
    insurances = order.Insurance.filter(item =>
      item['Insurance Type'] !== 'Self');
  }
  return (
    <Grid
      className={classes.root}
      item
      xs={otherProps['Grid Width']}
    >
      {otherProps['Question Type'] === 'Patient General' &&
        <Fragment>
          <Typography
            className={classes.typographyLabel}
            color="secondary"
            variant="subheading"
          >
            Patient Information
          </Typography>
          <Table className={classes.tablePatientGeneral}>
            <TableBody>
              <TableRow className={classes.tableRow}>
                <TableCell className={classes.tableCell}>
                  <Typography color="secondary">
                    Patient Name
                  </Typography>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {patient &&
                    <Typography>
                      {patient['First Name']}, {patient['Last Name']}
                    </Typography>
                  }
                </TableCell>
              </TableRow>
              <TableRow className={classes.tableRow}>
                <TableCell className={classes.tableCell}>
                  <Typography color="secondary">
                    {patient && patient['Date of Death'] ? 'Date of Birth' : 'Date of Birth, Age'}
                  </Typography>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {patient &&
                    <Typography>
                      {patient['Date of Birth']}, {' '}
                      {patientDobToAge(patient['Date of Birth'])}
                    </Typography>
                  }
                </TableCell>
              </TableRow>
              {patient && patient['Date of Death'] &&
                <TableRow className={classes.tableRow}>
                  <TableCell className={classes.tableCell}>
                    <Typography color="secondary">
                      Date of Death
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography>
                      {patient['Date of Death']}
                    </Typography>
                  </TableCell>
                </TableRow>
              }
              <TableRow className={classes.tableRow}>
                <TableCell className={classes.tableCell}>
                  <Typography color="secondary">
                    Gender
                  </Typography>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {patient &&
                    <Typography>
                      {patient.Gender}
                    </Typography>
                  }
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Fragment>
      }
      {otherProps['Question Type'] === 'Patient Insurance' &&
        <Fragment>
          <Typography
            className={classes.typographyLabel}
            color="secondary"
            variant="subheading"
          >
            Patient Insurance
          </Typography>
          {(patient || order) &&
            <div>
              {insurances.length > 0 ? (
                <Table
                  className={classNames(classes.table, {
                    [classes.tableNarrow]: otherProps['Grid Width'] !== 12,
                  })}
                >
                  <TableBody>
                    {sortInsurance(insurances).map(item => (
                      <TableRow
                        key={item.ID}
                        className={classes.tableRow}
                      >
                        <TableCell className={classes.tableCell}>
                          <Typography>
                            {item['Insurance Info']['Payer Name']}
                          </Typography>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Typography>
                            {item['Order Benefits']}
                          </Typography>
                        </TableCell>
                        <TableCell
                          className={classes.tableCell}
                          numeric
                        >
                          <Typography
                            className={classes.typographyInline}
                            color="secondary"
                          >
                            ID:
                            {' '}
                          </Typography>
                          <Typography
                            className={classes.typographyInline}
                          >
                            {item['Insurance ID']}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="secondary">
                  Patient has no insurance
                </Typography>
              )}
            </div>
          }
        </Fragment>
      }
      {otherProps['Question Type'] === 'Patient Vitals' &&
        <Fragment>
          <Typography
            className={classes.typographyLabel}
            color="secondary"
            variant="subheading"
            gutterBottom
          >
            Patient Vitals
          </Typography>
          {(patient || order) &&
            <div>
              {vitals.length > 0 ? (
                <Table
                  className={classNames(classes.table, {
                    [classes.tableNarrow]: otherProps['Grid Width'] !== 12,
                  })}
                >
                  <TableBody>
                    {vitals.map(item => (
                      <TableRow
                        key={item.ID}
                        className={classes.tableRow}
                      >
                        <TableCell
                          className={classNames(classes.tableCell, classes.tableCellVitals)}
                        >
                          <Typography
                            className={classes.typographyInline}
                            color="secondary"
                          >
                            Weight
                          </Typography>
                          {'\u00A0'}{'\u00A0'}
                          <Typography className={classes.typographyInline}>
                            {item.Weight} lb
                          </Typography>
                        </TableCell>
                        <TableCell
                          className={classNames(classes.tableCell, classes.tableCellVitals)}
                        >
                          <Typography
                            className={classes.typographyInline}
                            color="secondary"
                          >
                            Height
                          </Typography>
                          {'\u00A0'}{'\u00A0'}
                          <Typography className={classes.typographyInline}>
                            {item['Height Ft']}ft,{'\u00A0'}
                          </Typography>
                          <Typography className={classes.typographyInline}>
                            {item['Height In']}in
                          </Typography>
                        </TableCell>
                        <TableCell
                          className={classNames(classes.tableCell, classes.tableCellVitals)}
                        >
                          <Typography
                            className={classes.typographyInline}
                            color="secondary"
                          >
                            BMI
                          </Typography>
                          {'\u00A0'}{'\u00A0'}
                          <Typography className={classes.typographyInline}>
                            {item.BMI}
                          </Typography>
                        </TableCell>
                        <TableCell
                          className={classes.tableCell}
                          numeric
                        >
                          <Typography color="secondary">
                            {item['Create At']}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="secondary">
                  Patient has no vitals
                </Typography>
              )}
            </div>
          }
        </Fragment>
      }
      {otherProps['Question Type'] === 'Patient Prescribers' &&
        <Fragment>
          <Typography
            className={classes.typographyLabel}
            color="secondary"
            variant="subheading"
            gutterBottom
          >
            Prescribers Information
          </Typography>
          {(patient || order) &&
            <div>
              {prescribers.length > 0 ? (
                <Table
                  className={classNames(classes.table, {
                    [classes.tableNarrow]: otherProps['Grid Width'] !== 12,
                  })}
                >
                  <TableBody>
                    {prescribers.map(item => (
                      <TableRow
                        key={item.ID}
                        className={classes.tableRow}
                      >
                        <TableCell className={classes.tableCell}>
                          <Typography>
                            {item['First Name']} {item['Last Name']}
                          </Typography>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Typography color="secondary">
                            {item.Email}
                          </Typography>
                          {otherProps['Grid Width'] !== 12 &&
                            <Typography color="secondary">
                              {item.Phone}
                            </Typography>
                          }
                        </TableCell>
                        {otherProps['Grid Width'] !== 12 &&
                          <TableCell
                            className={classes.tableCell}
                            numeric
                          >
                            {item.NPI &&
                              <div>
                                <Typography
                                  className={classes.typographyInline}
                                  color="secondary"
                                >
                                  NPI:
                                  {' '}
                                </Typography>
                                <Typography className={classes.typographyInline}>
                                  {item.NPI}
                                </Typography>
                              </div>
                            }
                            {item.LIC &&
                              <div>
                                <Typography
                                  className={classes.typographyInline}
                                  color="secondary"
                                >
                                  LIC:
                                  {' '}
                                </Typography>
                                <Typography className={classes.typographyInline}>
                                  {item.LIC}
                                </Typography>
                              </div>
                            }
                          </TableCell>
                        }
                        {otherProps['Grid Width'] === 12 &&
                          <Fragment>
                            <TableCell
                              className={classes.tableCell}
                              numeric
                            >
                              <Typography color="secondary">
                                {item.Phone}
                              </Typography>
                            </TableCell>
                            <TableCell
                              className={classes.tableCell}
                              numeric
                            >
                              {item.NPI &&
                                <Fragment>
                                  <Typography
                                    className={classes.typographyInline}
                                    color="secondary"
                                  >
                                    NPI:
                                    {' '}
                                  </Typography>
                                  <Typography className={classes.typographyInline}>
                                    {item.NPI}
                                  </Typography>
                                </Fragment>
                              }
                            </TableCell>
                            <TableCell
                              className={classes.tableCell}
                              numeric
                            >
                              {item.LIC &&
                                <Fragment>
                                  <Typography
                                    className={classes.typographyInline}
                                    color="secondary"
                                  >
                                    LIC:
                                    {' '}
                                  </Typography>
                                  <Typography className={classes.typographyInline}>
                                    {item.LIC}
                                  </Typography>
                                </Fragment>
                              }
                            </TableCell>
                          </Fragment>
                        }
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="secondary">
                  Patient has no prescribers
                </Typography>
              )}
            </div>
          }
        </Fragment>
      }
      {otherProps['Question Type'] === 'Patient Diagnosis' &&
        <Fragment>
          <Typography
            className={classes.typographyLabel}
            color="secondary"
            variant="subheading"
            gutterBottom
          >
            Diagnoses
          </Typography>
          {(patient || order) &&
            <div>
              {diagnoses.length > 0 ? (
                <Table
                  className={classNames(classes.table, {
                    [classes.tableNarrow]: otherProps['Grid Width'] !== 12,
                  })}
                >
                  <TableBody>
                    {diagnoses.map(item => (
                      <TableRow
                        key={item.ID}
                        className={classes.tableRow}
                      >
                        <TableCell className={classes.tableCell}>
                          <Typography>
                            {item['DX Code']}
                          </Typography>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Typography color="secondary">
                            {item['Long Description']}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="secondary">
                  Patient has no diagnoses
                </Typography>
              )}
            </div>
          }
        </Fragment>
      }
      {otherProps['Question Type'] === 'Signature' &&
        <Fragment>
          {Text &&
            <Typography
              className={classes.typographySignatureText}
              color="secondary"
            >
              {Text}
            </Typography>
          }
          <Typography
            className={classes.typographyLabelPlaceholder}
            variant="caption"
          >
            Physician Signature
          </Typography>
          <div className={classes.typographyAnswerPlaceholder}>
            <Field
              id={name}
              name={name}
              component={TemplatePreviewFillFormFieldSignature}
            />
          </div>
          <Typography
            className={classes.typographyDateStampPlaceholder}
            align="right"
            variant="caption"
          >
            Date Stamp for Signature
          </Typography>
        </Fragment>
      }
      {(otherProps['Question Type'] === 'Textbox' && Text) &&
        <Typography
          className={classes.typographyLabelPlaceholder}
          variant="caption"
        >
          {Text.split('\n').map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={index}>
              {item}
              <br />
            </Fragment>
          ))}
        </Typography>
      }
      {otherProps['Question Type'] === 'MultiLine' &&
        <Field
          id={name}
          name={name}
          component={TextField}
          {...Text ? { label: Text } : {}}
          multiline
          fullWidth
        />
      }
      {otherProps['Question Type'] === 'Date' &&
        <Field
          id={name}
          name={name}
          component={DatepickerField}
          {...Text ? { label: Text } : {}}
          validate={[validate.date]}
          normalize={normalize.date}
          minDate="01/01/1900"
          maxDate="01/01/2100"
          dateFormat={dateFormat}
          placeholder="mm/dd/yyyy"
          maxLength="10"
          endIcon="start"
        />
      }
      {otherProps['Question Type'] === 'Checkbox' &&
        <FormControl component="fieldset">
          {Text &&
            <FormLabel component="legend">{Text}</FormLabel>
          }
          {Choices.map(option => (
            <Field
              className={classes.checkboxField}
              key={option}
              id={`${name}${option}`}
              name={`${name}${option}`}
              component={CheckboxField}
              label={option}
            />
          ))}
        </FormControl>
      }
      {otherProps['Question Type'] === 'Radio' && Choices &&
        <FormControl component="fieldset">
          {Text &&
            <FormLabel component="legend">{Text}</FormLabel>
          }
          <Field
            name={name}
            component={RadioGroupField}
            direction="column"
          >
            {Choices.map(option => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio color="primary" />}
                label={option}
              />
            ))}
          </Field>
        </FormControl>
      }
      {otherProps['Question Type'] === 'DropDown' && Choices &&
        <Field
          className={classes.selectMenuField}
          id={name}
          name={name}
          component={SelectMenuField}
          {...Text ? { label: Text } : {}}
          options={Choices.map(item => ({
            value: item,
            label: item,
          }))}
          size="lg"
        />
      }
      {(otherProps['Question Type'] === 'HCPCS Codes' && order && order['Order Line'].length > 0) &&
        <Fragment>
          <Typography
            className={classes.typographyLabel}
            color="secondary"
            variant="subheading"
            gutterBottom
          >
            HCPCS Codes
          </Typography>
          <div>
            <Table
              className={classNames(classes.table, {
                [classes.tableNarrow]: otherProps['Grid Width'] !== 12,
              })}
            >
              <TableBody>
                {order['Order Line'].map(item => (
                  <TableRow
                    key={item.ID}
                    className={classes.tableRow}
                  >
                    <TableCell className={classes.tableCell}>
                      <Typography>
                        {item['HCPC S Code']['HCPC S Code']}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography color="secondary">
                        {item['Description']}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography>
                        *{item.Units}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Fragment>
      }
      {otherProps['Question Type'] === 'Company Information' &&
        <Fragment>
          <Typography
            className={classes.typographyLabel}
            color="secondary"
            variant="subheading"
            gutterBottom
          >
            Company Information
          </Typography>
          <div>
            {(business && business.ID) ? (
              <Table
                className={classNames(classes.table, {
                  [classes.tableNarrow]: otherProps['Grid Width'] !== 12,
                })}
              >
                <TableBody>
                  <TableRow
                    className={classes.tableRow}
                  >
                    <TableCell className={classes.tableCell}>
                      <Typography>
                        Company Name
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography color="secondary">
                        {business['Company Info']['Company Name']}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {business['Company Identify']['Practice NPI'] &&
                        <Fragment>
                          <Typography
                            className={classes.typographyInline}
                            color="secondary"
                          >
                            NPI:
                            {' '}
                          </Typography>
                          <Typography className={classes.typographyInline}>
                            {business['Company Identify']['Practice NPI']}
                          </Typography>
                        </Fragment>
                      }
                    </TableCell>
                  </TableRow>
                  <TableRow
                    className={classes.tableRow}
                  >
                    <TableCell className={classes.tableCell}>
                      <Typography>
                        Address
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell} colSpan="2">
                      <Typography color="secondary">
                        {getBusinessAddress({ business })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <Typography color="secondary">
                No information available
              </Typography>
            )}
          </div>
        </Fragment>
      }
      {(otherProps['Question Type'] === 'Company Logo' && business && business.Image && business.Image.ID) &&
        <div className={classes.logoImageContainer}>
          <img
            className={classes.logoImage}
            alt="Company Logo"
            src={`${business.Image['File Url']}${business.Image['File Name']}`}
          />
        </div>
      }
    </Grid>
  );
};

TemplatePreviewFillFormField.propTypes = {
  name: PropTypes.string.isRequired,
  'Question Type': PropTypes.string.isRequired,
  Text: PropTypes.string,
  'Grid Width': PropTypes.number.isRequired,
  Choices: PropTypes.arrayOf(PropTypes.string),
  patient: PropTypes.shape({
    'Record Number': PropTypes.number.isRequired,
    'First Name': PropTypes.string.isRequired,
    'Last Name': PropTypes.string.isRequired,
    'Date of Birth': PropTypes.string.isRequired,
    Gender: PropTypes.string.isRequired,
    'Insurance Information': PropTypes.arrayOf(PropTypes.shape({
      'Insurance Info': PropTypes.shape({
        'Payer Name': PropTypes.string,
      }),
    })),
    Vitals: PropTypes.arrayOf(PropTypes.shape({
      ID: PropTypes.number,
    })),
    Prescriber: PropTypes.arrayOf(PropTypes.shape({
      ID: PropTypes.number,
    })),
    Diagnosis: PropTypes.arrayOf(PropTypes.shape({
      ID: PropTypes.number,
    })),
  }),
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  classes: PropTypes.shape({
    typographyLabel: PropTypes.string.isRequired,
    typographySignatureText: PropTypes.string.isRequired,
    typographyLabelPlaceholder: PropTypes.string.isRequired,
    typographyDateStampPlaceholder: PropTypes.string.isRequired,
    typographyPatientInformationPlaceholder: PropTypes.string.isRequired,
    typographyAnswerPlaceholder: PropTypes.string.isRequired,
    selectMenuField: PropTypes.string.isRequired,
    typographyInline: PropTypes.string.isRequired,
    tablePatientGeneral: PropTypes.string.isRequired,
    table: PropTypes.string.isRequired,
    tableNarrow: PropTypes.string.isRequired,
    tableRow: PropTypes.string.isRequired,
    tableCell: PropTypes.string.isRequired,
    tableCellVitals: PropTypes.string.isRequired,
  }).isRequired,
};

TemplatePreviewFillFormField.defaultProps = {
  patient: null,
  Choices: null,
  Text: null,
  business: null,
};

export default withStyles(styles)(TemplatePreviewFillFormField);



// WEBPACK FOOTER //
// ./src/components/settings/TemplatePreviewFillForm/TemplatePreviewFillFormField.js